import React from 'react';
import { Modal, Button, Descriptions, Tag, Alert, Divider } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { ShiftClosingDto } from '../../types/cashRegister';
import { useCurrency } from '../../hooks/useCurrency';
import { useResponsive } from '../../hooks/useResponsive';
import { useRestaurantTimeZone } from '../../hooks/useTimeZone';
import ShiftClosingAmounts, { getDifferenceColor, getDifferencePrefix } from './ShiftClosingAmounts';

interface ViewShiftClosingModalProps {
  visible: boolean;
  shiftClosing: ShiftClosingDto | null;
  onClose: () => void;
}

const ViewShiftClosingModal: React.FC<ViewShiftClosingModalProps> = ({
  visible,
  shiftClosing,
  onClose,
}) => {
  const { format: formatCurrency } = useCurrency();
  const { isMobile } = useResponsive();
  const { formatInTimeZone } = useRestaurantTimeZone();

  if (!shiftClosing) return null;

  const getStatusTag = (status: string) => {
    const statusMap: { [key: string]: { color: string; text: string } } = {
      Pending: { color: 'orange', text: 'Pendiente' },
      Approved: { color: 'green', text: 'Aprobado' },
      Rejected: { color: 'red', text: 'Rechazado' },
    };
    const statusInfo = statusMap[status] || { color: 'default', text: status };
    return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
  };

  const renderDifference = (diff: number) => (
    <div style={{ marginTop: 8 }}>
      Diferencia:{' '}
      <span style={{ color: getDifferenceColor(diff), fontWeight: 'bold' }}>
        {getDifferencePrefix(diff)}{formatCurrency(Math.abs(diff))}
        {diff === 0 ? ' ✓' : ''}
      </span>
    </div>
  );

  return (
    <Modal
      title="Detalle de Cierre de Turno"
      open={visible}
      onCancel={onClose}
      width={isMobile ? '100%' : 900}
      style={isMobile ? { top: 0, paddingBottom: 0, maxWidth: '100vw' } : {}}
      bodyStyle={isMobile ? { maxHeight: 'calc(100vh - 110px)', overflowY: 'auto', padding: '12px' } : {}}
      footer={[
        <Button key="close" onClick={onClose} icon={<CloseOutlined />} block={isMobile}>
          Cerrar
        </Button>,
      ]}
      destroyOnClose
    >
      <Descriptions bordered size="small" column={isMobile ? 1 : 2}>
        <Descriptions.Item label="Empleado">
          {shiftClosing.createdByUserName}
        </Descriptions.Item>
        <Descriptions.Item label="Fecha Creación">
          {formatInTimeZone(shiftClosing.createdAt, 'DD/MM/YYYY HH:mm')}
        </Descriptions.Item>
        <Descriptions.Item label="Estado">
          {getStatusTag(shiftClosing.status)}
        </Descriptions.Item>
        {shiftClosing.reviewedByUserName && (
          <>
            <Descriptions.Item label="Revisado Por">
              {shiftClosing.reviewedByUserName}
            </Descriptions.Item>
            <Descriptions.Item label="Fecha Revisión">
              {shiftClosing.reviewedAt ? formatInTimeZone(shiftClosing.reviewedAt, 'DD/MM/YYYY HH:mm') : '-'}
            </Descriptions.Item>
          </>
        )}
      </Descriptions>

      <Divider orientation="left">{isMobile ? 'Montos' : 'Comparación de Montos'}</Divider>

      <ShiftClosingAmounts
        shiftClosing={shiftClosing}
        isMobile={isMobile}
        formatCurrency={formatCurrency}
        renderDifference={renderDifference}
      />

      {shiftClosing.employeeNotes && (
        <>
          <Divider orientation="left">Notas del Empleado</Divider>
          <Alert message={shiftClosing.employeeNotes} type="info" showIcon />
        </>
      )}

      {shiftClosing.reviewNotes && (
        <>
          <Divider orientation="left">Notas del Revisor</Divider>
          <Alert
            message={shiftClosing.reviewNotes}
            type={shiftClosing.status === 'Approved' ? 'success' : 'error'}
            showIcon
          />
        </>
      )}
    </Modal>
  );
};

export default ViewShiftClosingModal;
